import React from 'react';

import useWebSocket, { ReadyState } from 'react-use-websocket';

import { WS_ENDPOINT } from 'src/config';
import AuthContext from 'src/features/auth/context/AuthContext';
import WSContext, { WebSocketGroup, type Subscriptions } from 'src/features/visitor/context/WSContext';
import { WebSocketEvent } from 'src/features/visitor/types';
import { useParking } from 'src/providers/ParkingProvider';

// WS_ENDPOINT is configured with a trailing slash, so trim it before appending the parking id.
const buildSocketUrl = (parkingId: string) => (parkingId ? `${WS_ENDPOINT.replace(/\/+$/, '')}/${parkingId}/` : null);

export function WSProvider({ children }: { children: React.ReactNode }) {
    const { user } = React.useContext(AuthContext);
    const { parkingId } = useParking();

    // The socket url intentionally lags one render behind `parkingId`: it keeps us connected to the
    // previous parking long enough to unsubscribe there before the hook reconnects to the new one.
    const [socketUrl, setSocketUrl] = React.useState<string | null>(() => buildSocketUrl(parkingId));

    const { lastJsonMessage, sendJsonMessage, readyState } = useWebSocket<{ group?: WebSocketGroup }>(
        socketUrl,
        {},
        !!user,
    );
    const subscriptions = React.useRef<Subscriptions>({});
    const pendingResubscribe = React.useRef(false);

    const activeGroups = React.useCallback(
        () => Object.keys(subscriptions.current).filter((group) => subscriptions.current[group].length),
        [],
    );

    const subscribe = React.useCallback(
        (group: WebSocketGroup, callback: (data: WebSocketEvent) => void) => {
            const subs = subscriptions.current[group];

            if (!subs || !subs.length) {
                sendJsonMessage({
                    action: 'subscribe',
                    group,
                });
                subscriptions.current[group] = [];
            }

            subscriptions.current[group].push(callback);
        },
        [sendJsonMessage],
    );

    const unsubscribe = React.useCallback(
        (group: WebSocketGroup, callback: (data: WebSocketEvent) => void) => {
            const subs = subscriptions.current[group];
            if (!subs) return;
            subscriptions.current[group].splice(
                subscriptions.current[group].findIndex((i) => i === callback),
                1,
            );
            if (!subscriptions.current[group].length) {
                sendJsonMessage({
                    action: 'unsubscribe',
                    group,
                });
            }
        },
        [sendJsonMessage],
    );

    // Parking switched: drop the groups on the connection we are still on, then point the hook at the
    // new parking's endpoint. The subscription callbacks themselves are kept so they can be re-sent.
    React.useEffect(() => {
        const nextUrl = buildSocketUrl(parkingId);
        if (nextUrl === socketUrl) return;

        activeGroups().forEach((group) => {
            // `keep: false` – never queue these, or they would be delivered to the *new* socket and
            // immediately undo the re-subscribe below.
            sendJsonMessage({ action: 'unsubscribe', group }, false);
        });

        pendingResubscribe.current = true;
        setSocketUrl(nextUrl);
    }, [parkingId, socketUrl, activeGroups, sendJsonMessage]);

    // Re-announce the same groups once the new parking's connection is up.
    React.useEffect(() => {
        if (readyState !== ReadyState.OPEN || !pendingResubscribe.current) return;

        pendingResubscribe.current = false;
        activeGroups().forEach((group) => {
            sendJsonMessage({ action: 'subscribe', group }, false);
        });
    }, [readyState, activeGroups, sendJsonMessage]);

    React.useEffect(() => {
        const group = lastJsonMessage?.group;
        if (group && subscriptions.current[group]) {
            subscriptions.current[group].map((callback) => {
                callback(lastJsonMessage);
            });
        }
    }, [lastJsonMessage]);

    return (
        <WSContext.Provider
            value={{
                subscribe,
                unsubscribe,
            }}
        >
            {children}
        </WSContext.Provider>
    );
}
